import React, { Suspense, lazy, useEffect } from 'react';
import './styles/theme-system.css';
import './styles/modern-enhancements.css';
import './styles/animations.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { IntegrationsProvider } from './contexts/IntegrationsContext';
import { UserProvider } from './contexts/UserContext';
import { BrandProvider } from './contexts/BrandContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { FeatureFlagProvider } from './contexts/FeatureFlagContext';
import ProtectedRoute from './components/ProtectedRoute';
import BillingGate, { BillingBanner } from './components/BillingGate';
import SimplePermissionRoute from './components/SimplePermissionRoute';
import { MODULES } from './utils/simplePermissions';
import Layout from './components/Layout/Layout';
import DashboardRouter from './pages/DashboardRouter';
import DevToolsErrorBoundary from './components/DevToolsErrorBoundary';
import devToolsService from './services/DevToolsService';
import remoteDebugService from './services/RemoteDebugService';
import SecurityService from './services/SecurityService';
// Disabled: realTimeErrorFixer was causing "Cannot read properties of undefined (reading 'call')" errors
// import './utils/realTimeErrorFixer';
import { supabase } from './utils/supabaseClient';
import { getCurrentDeploymentType } from './config/deploymentConfig.js';
import { ToastContainer } from './components/Common/Toast';
import ConnectionBanner from './components/Common/ConnectionBanner';
import ReportProblemButton from './components/Common/ReportProblemButton';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';

// Small/auth pages (direct)
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import SetPassword from './pages/SetPassword';
import ForgotPassword from './pages/ForgotPassword';
import Unauthorized from './pages/Unauthorized';
import Logout from './pages/Logout';
// CompanyOnboarding removed - now handled by separate Admin Dashboard
import GoogleOAuthCallback from './pages/GoogleOAuthCallback';
import StripeConnectCallback from './pages/StripeConnectCallback';

import OnboardingTest from './pages/OnboardingTest';
import ResumeOnboarding from './pages/ResumeOnboarding';
import SchemaTest from './components/SchemaTest';

// Medium pages (direct)
import Employees from './pages/Employees';
import Timesheets from './pages/Timesheets';
import MyTime from './pages/MyTime';
import MyTimeOff from './pages/MyTimeOff';
import AdminApprovals from './pages/AdminApprovals';
import AdminTimeOff from './pages/AdminTimeOff';
import Labor from './pages/Labor';
import RateCards from './pages/Settings/RateCards';
import FieldJobView from './pages/FieldJobView';

// Board #456. Time-off notifications linked to /pto?id=..., which was never a route, so every one
// opened a dead page. New ones link to the Time Off tab directly (migration
// 20260924_time_off_notification_wording); this keeps the ones already sent working.
const PtoRedirect = () => {
  const id = new URLSearchParams(useLocation().search).get('id');
  return <Navigate to={`/employees?tab=time-off${id ? `&id=${encodeURIComponent(id)}` : ''}`} replace />;
};



// Lazy-loaded heavy pages
const AgingReport = lazy(() => import('./pages/AgingReport'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Scheduling = lazy(() => import('./pages/Scheduling'));
const Quotes = lazy(() => import('./pages/QuotesPro'));
const Jobs = lazy(() => import('./pages/Jobs'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Disputes = lazy(() => import('./pages/Disputes'));
const JobsHistory = lazy(() => import('./pages/JobsHistory'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const Customers = lazy(() => import('./pages/Customers'));
const Documents = lazy(() => import('./pages/Documents'));
const Settings = lazy(() => import('./pages/Settings'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Expenses = lazy(() => import('./pages/Expenses'));
const PurchaseOrders = lazy(() => import('./pages/PurchaseOrders'));
const Vendors = lazy(() => import('./pages/Vendors'));
const AdvancedReports = lazy(() => import('./pages/AdvancedReports'));
const Notifications = lazy(() => import('./pages/Notifications'));
const CloudStorage = lazy(() => import('./pages/CloudStorage'));
const CRM = lazy(() => import('./pages/CRM'));
const Messages = lazy(() => import('./pages/Messages'));
const TeamChat = lazy(() => import('./pages/TeamChat'));
const Reports = lazy(() => import('./pages/Reports'));
const Tools = lazy(() => import('./pages/Tools'));
const JobTemplates = lazy(() => import('./pages/JobTemplates'));
const PriceBook = lazy(() => import('./pages/PriceBook'));
const CustomerScheduling = lazy(() => import('./pages/CustomerScheduling'));
const MyProfile = lazy(() => import('./pages/MyProfile'));

// Additional Feature Pages
// Placeholder pages removed — these were Coming Soon shells with no functionality
const PaymentProcessing = lazy(() => import('./pages/PaymentProcessing'));
// Removed AdvancedScheduling - already implemented and industry-leading

// Service Request System
// IncomingRequests (lead inbox at /leads) retired — consolidated into the
// Marketplace Browse surface. The page file remains as orphaned dead code; its
// routes now redirect to /marketplace.
const PortalQuote = lazy(() => import('./pages/PortalQuote'));
const PublicQuoteView = lazy(() => import('./pages/PublicQuoteView'));
const RefactoredCustomerPortal = lazy(() => import('./pages/RefactoredCustomerPortal'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const Booking = lazy(() => import('./pages/Booking'));

// Public Marketplace (No Auth Required)
const PublicMarketplace = lazy(() => import('./pages/PublicMarketplace'));
const PublicRequestDetails = lazy(() => import('./pages/PublicRequestDetails'));

// Initialize developer tools and debugging services (development only)
const initializeDevTools = async () => {
  // Only initialize dev tools in development
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  try {
    // Initialize DevTools Service
    await devToolsService.initialize();

    // Initialize Remote Debug Service (optional, disabled by default)
    const wsUrl = process.env.REACT_APP_DEBUG_WS_URL;
    if (wsUrl && wsUrl.trim() !== '') {
      try {
        await remoteDebugService.initialize({
          url: wsUrl,
          autoReconnect: true
        });
      } catch (error) {
        console.warn('Remote Debug Service failed to initialize:', error.message);
      }
    }
  } catch (error) {
    console.warn('Developer tools initialization failed:', error);
  }
};

/**
 * Error boundary scoped to the CURRENT ROUTE.
 *
 * The app-level boundary wraps everything with a hardcoded componentName="App", so
 * any render error anywhere reported "Error in App component" — which localises
 * nothing (App.js is rarely the culprit) and blanks the entire UI including the nav.
 *
 * This sits inside Layout, so a failing screen leaves the shell usable, and reports
 * the actual route path. Keying on pathname remounts the boundary on navigation, so
 * a user can browse away from a broken screen instead of being stuck until reload.
 */
function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return (
    <DevToolsErrorBoundary key={location.pathname} componentName={location.pathname || 'route'}>
      {children}
    </DevToolsErrorBoundary>
  );
}

function App() {
  useEffect(() => {
    // Listen for auth state changes to handle security cleanup
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' && !session) {
        // End security session tracking on logout
        const sessionToken = localStorage.getItem('session_token');
        if (sessionToken) {
          SecurityService.endSession(sessionToken, 'logout');
        }
      }
    });

    // Listen for session expiry events
    // An AUTOMATIC sign-out ends only this browser's session (scope 'local'). The default
    // scope is 'global', which revoked the user's sessions on every other device too. Awaited
    // so the local session is actually cleared before the hard navigation unloads the page;
    // capped at 5s so a stalled request can never keep the user on a dead page.
    const handleSessionExpired = async () => {
      try {
        await Promise.race([
          supabase.auth.signOut({ scope: 'local' }),
          new Promise((resolve) => setTimeout(resolve, 5000)),
        ]);
      } finally {
        window.location.href = '/login';
      }
    };
    window.addEventListener('session-expired', handleSessionExpired);

    // Initialize developer tools on app start
    initializeDevTools();



    // Make devLogger globally available for error boundary
    if (window.devLogger) {
      window.devLogger.addLog('INFO', 'TradeMate Pro application started', 'app');
    }

    // Cleanup auth listener on unmount
    return () => {
      subscription.unsubscribe();
      window.removeEventListener('session-expired', handleSessionExpired);
      SecurityService.stopSessionMonitoring();
    };
  }, []);

  const deploymentType = getCurrentDeploymentType();

  return (
    <DevToolsErrorBoundary componentName="App">
      <QueryClientProvider client={queryClient}>
        <UserProvider>
          <BrandProvider>
            <IntegrationsProvider>
              <ThemeProvider>
                <FeatureFlagProvider deploymentType={deploymentType}>
                <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                  <ToastContainer />
                  <ConnectionBanner />
                  <ReportProblemButton />

              {/* The fallback must PAINT and must fill the viewport.
                  It used to be `<div className="p-6 text-gray-600">Loading…</div>` — no
                  background and no height. Whenever it renders (cold route, cache miss,
                  slow network) the whole routed area collapsed to a ~1100px unstyled
                  strip over the bare body, which reads as a flash on navigation. The
                  Router sets v7_startTransition, so on a WARM chunk React holds the old
                  page and this never shows; it is the cold path that was unstyled.
                  bg-gray-50 dark:bg-gray-900 matches the body rule in index.css. */}
              <Suspense fallback={(
                <div
                  className="min-h-screen w-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center"
                  aria-busy="true"
                  aria-live="polite"
                >
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                  <span className="sr-only">Loading…</span>
                </div>
              )}>

          <Routes>
            {/* Landing page - shown to unauthenticated users */}
            <Route path="/" element={<Landing />} />

            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="/oauth/google/callback" element={<GoogleOAuthCallback />} />
            <Route path="/oauth/callback/stripe" element={<StripeConnectCallback />} />
            <Route path="/schedule-appointment" element={<CustomerScheduling />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/portal/quote/:id" element={<PortalQuote />} />
            <Route path="/portal/quote/view/:token" element={<PublicQuoteView />} />

            {/* Public Marketplace - No Auth Required */}
            <Route path="/browse-jobs" element={<PublicMarketplace />} />
            <Route path="/browse-jobs/:requestId" element={<PublicRequestDetails />} />
            <Route path="/marketplace/public" element={<PublicMarketplace />} />
            <Route path="/marketplace/public/:requestId" element={<PublicRequestDetails />} />

            {/* Refactored Customer Portal (served from public/customer-portal/build) */}
            <Route path="/customer-portal/*" element={<RefactoredCustomerPortal />} />

            {/* /onboarding — resume setup after skipping it. ProtectedRoute decides who is
                FORCED into the wizard; this is how someone CHOOSES to go back. Without it,
                "Skip for now" was a one-way door: the skip persists to company_settings, so
                is_onboarding_complete answers true forever and no URL could render the
                wizard again. */}
            <Route path="/onboarding" element={<ResumeOnboarding />} />

            {/* New Onboarding System Test */}
            <Route path="/onboarding-test" element={<OnboardingTest />} />
            <Route path="/schema-test" element={<SchemaTest />} />

            {/* Onboarding is now integrated into ProtectedRoute */}

            {/* Protected routes */}
            <Route path="/*" element={
              <ProtectedRoute>
                <BillingGate>
                <Layout>
                  <BillingBanner />
                  <RouteErrorBoundary>
                  <Routes>
                    {/* Core routes with module access checks */}
                    <Route path="/" element={<DashboardRouter />} />
                    <Route path="/dashboard" element={<DashboardRouter />} />
                    <Route path="/my-dashboard" element={<DashboardRouter />} />
                    {/* Platform-owner tooling moved to the standalone admin app. This route
                        keeps working for contractor owners/admins, who reach their own
                        business dashboard through DashboardRouter. */}
                    <Route path="/admin-dashboard" element={<DashboardRouter />} />
                    <Route path="/jobs" element={
                      <SimplePermissionRoute module={MODULES.JOBS}>
                        <Jobs />
                      </SimplePermissionRoute>
                    } />
                    {/* One job, one address. The office previously had no per-job URL at
                        all — only /jobs?edit=<id>, which opens the EDITOR — so a job could
                        not be linked, bookmarked or simply looked at. Gated on the same
                        MODULES.JOBS as the list; who may EDIT is decided inside the page by
                        seat tier. Static siblings like /jobs/history still win: React Router
                        ranks a literal segment above a dynamic one regardless of order. */}
                    <Route path="/jobs/:id" element={
                      <SimplePermissionRoute module={MODULES.JOBS}>
                        <JobDetail />
                      </SimplePermissionRoute>
                    } />
                    {/* Legacy route redirects */}
                    <Route path="/jobs/active" element={<Navigate to="/jobs" replace />} />
                    <Route path="/jobs/closed" element={<Navigate to="/jobs/history" replace />} />
                    <Route path="/pto" element={<PtoRedirect />} />
                    <Route path="/job-templates" element={<Navigate to="/templates" replace />} />
                    <Route path="/field-job/:jobId" element={<FieldJobView />} />
                    <Route path="/calendar" element={
                      <SimplePermissionRoute module={MODULES.CALENDAR}>
                        <Calendar />
                      </SimplePermissionRoute>
                    } />
                    <Route path="/scheduling" element={
                      <SimplePermissionRoute module={MODULES.CALENDAR}>
                        <Scheduling />
                      </SimplePermissionRoute>
                    } />
                    <Route path="/documents" element={
                      <SimplePermissionRoute module={MODULES.DOCUMENTS}>
                        <Documents />
                      </SimplePermissionRoute>
                    } />

                    {/* Marketplace route */}
                    <Route path="/marketplace" element={
                      <SimplePermissionRoute module={MODULES.MARKETPLACE}>
                        <Marketplace />
                      </SimplePermissionRoute>
                    } />

                    {/* Booking route - unified booking system */}
                    <Route path="/booking" element={
                      <SimplePermissionRoute module={MODULES.MARKETPLACE}>
                        <Booking />
                      </SimplePermissionRoute>
                    } />

                    {/* Admin routes with module access checks */}
                    <Route path="/customers" element={
                      <SimplePermissionRoute module={MODULES.CUSTOMERS}>
                        <Customers />
                      </SimplePermissionRoute>
                    } />
                    <Route path="/quotes" element={
                      <SimplePermissionRoute module={MODULES.QUOTES}>
                        <Quotes />
                      </SimplePermissionRoute>
                    } />
                    <Route path="/templates" element={
                      <SimplePermissionRoute module={MODULES.QUOTES}>
                        <JobTemplates />
                      </SimplePermissionRoute>
                    } />
                    <Route path="/price-book" element={
                      <SimplePermissionRoute module={MODULES.QUOTES}>
                        <PriceBook />
                      </SimplePermissionRoute>
                    } />
                    {/* Removed Work Orders page; use Calendar for scheduling */}
                    <Route path="/invoices" element={
                      <SimplePermissionRoute module={MODULES.INVOICES}>
                        {require('./utils/features').FEATURES.INVOICING ? <Invoices /> : <div className="p-6 text-gray-600">Invoices disabled.</div>}
                      </SimplePermissionRoute>
                    } />
                    <Route path="/disputes" element={
                      <SimplePermissionRoute module={MODULES.INVOICES}>
                        <Disputes />
                      </SimplePermissionRoute>
                    } />
                    {/* Board #875. /disputes/<id> matched no route, so it fell through to the
                        catch-all and was silently redirected to the dashboard — a link to a
                        specific dispute quietly took you somewhere else. The list already
                        tracks an expanded dispute, so the id opens that one. */}
                    <Route path="/disputes/:disputeId" element={
                      <SimplePermissionRoute module={MODULES.INVOICES}>
                        <Disputes />
                      </SimplePermissionRoute>
                    } />
                    <Route path="/invoices/aging" element={
                      <SimplePermissionRoute module={MODULES.INVOICES}>
                        <AgingReport />
                      </SimplePermissionRoute>
                    } />
                    <Route path="/rate-cards" element={
                      <SimplePermissionRoute module={MODULES.RATE_CARDS}>
                        <RateCards />
                      </SimplePermissionRoute>
                    } />
                    <Route path="/purchase-orders" element={
                      <SimplePermissionRoute module={MODULES.PURCHASE_ORDERS}>
                        <PurchaseOrders />
                      </SimplePermissionRoute>
                    } />
                    <Route path="/vendors" element={
                      <SimplePermissionRoute module={MODULES.VENDORS}>
                        <Vendors />
                      </SimplePermissionRoute>
                    } />
                    <Route path="/advanced-reports" element={
                      <SimplePermissionRoute module={MODULES.REPORTS}>
                        <AdvancedReports />
                      </SimplePermissionRoute>
                    } />
                    <Route path="/employees" element={
                      <SimplePermissionRoute module={MODULES.EMPLOYEES}>
                        <Employees />
                      </SimplePermissionRoute>
                    } />
                    {/* Employee self-service routes */}
                    <Route path="/my/time" element={
                      <SimplePermissionRoute module={MODULES.TIMESHEETS}>
                        <MyTime />
                      </SimplePermissionRoute>
                    } />
                    <Route path="/my/time-off" element={
                      <SimplePermissionRoute module={MODULES.TIMESHEETS}>
                        <MyTimeOff />
                      </SimplePermissionRoute>
                    } />

                    {/* Admin routes */}
                    <Route path="/admin/approvals" element={
                      <SimplePermissionRoute module={MODULES.TIMESHEETS}>
                        <AdminApprovals />
                      </SimplePermissionRoute>
                    } />
                    <Route path="/admin/time-off" element={
                      <SimplePermissionRoute module={MODULES.TIMESHEETS}>
                        <AdminTimeOff />
                      </SimplePermissionRoute>
                    } />

                    {/* Legacy combined page retained temporarily */}
                    <Route path="/timesheets" element={
                      <SimplePermissionRoute module={MODULES.TIMESHEETS}>
                        <Timesheets />
                      </SimplePermissionRoute>
                    } />
                    {/* Labor page - manager/admin view of timesheet and labor data */}
                    <Route path="/labor" element={
                      <SimplePermissionRoute module={MODULES.LABOR}>
                        <Labor />
                      </SimplePermissionRoute>
                    } />
                    <Route path="/reports" element={
                      <SimplePermissionRoute module={MODULES.REPORTS}>
                        {require('./utils/features').FEATURES.REPORTS ? <Reports /> : <div className="p-6 text-gray-600">Reports disabled.</div>}
                      </SimplePermissionRoute>
                    } />

                    {/* Tools route - available to all users */}
                    <Route path="/tools" element={<Tools />} />

                    {/* Inventory route - Operations section */}
                    <Route path="/operations/inventory" element={
                      <SimplePermissionRoute module={MODULES.INVENTORY}>
                        <Inventory />
                      </SimplePermissionRoute>
                    } />
                    {/* Legacy/short URL: /inventory → canonical operations path (bookmarks/typed URLs) */}
                    <Route path="/inventory" element={<Navigate to="/operations/inventory?tab=items" replace />} />

                    {/* Board #1245/#1246. This said "available to all users" and meant it: no
                        wrapper, so a technician could open the company-to-customer inbox and send
                        to any customer on any job. Gated on the new CUSTOMER_MESSAGING permission
                        (operator decision 2026-09-20), which defaults on for
                        owner/admin/manager/supervisor/dispatcher/customer_service and off for crew.

                        The route gate is the UI half and a determined caller can skip it; the half
                        that holds is the RESTRICTIVE policy messages_crew_only_assigned_work, which
                        limits a crew seat to work orders assigned to them whatever this toggle
                        says. */}
                    <Route path="/messages" element={
                      <SimplePermissionRoute module={MODULES.CUSTOMER_MESSAGING}>
                        <Messages />
                      </SimplePermissionRoute>
                    } />

                    {/* Team Chat - internal staff communication. UNGATED ON PURPOSE, and it must
                        stay that way: "Team Chat is available to every active employee" is the
                        first sentence of the same decision. It is internal, so there is nothing
                        to protect, and it shares the `messages` table with customer messaging —
                        which is why the policy above tests recipient_type/sender_type rather than
                        just work_order_id, or this screen would have gone dark for every crew
                        member. */}
                    <Route path="/team-chat" element={<TeamChat />} />

                    {/* Integration routes */}
                    <Route path="/expenses" element={<Expenses />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/storage" element={<CloudStorage />} />
                    <Route path="/crm" element={<CRM />} />
                    {/* /automation unlinked by operator ruling 11, 2026-10-30: the page showed
                        hardcoded sample data. pages/Automation.js is kept (named in the
                        2026-10-29 settings reports); an old URL falls to the catch-all. */}

                    {/* Additional Feature Routes */}
                    {/* Coming Soon placeholder routes removed */}
                    <Route path="/coming-soon/payment-processing" element={<PaymentProcessing />} />
                    {/* Removed advanced-scheduling route - already implemented */}

                    {/* Lead inbox consolidated into the Marketplace Browse surface.
                        Both legacy routes now redirect to /marketplace. */}
                    <Route path="/leads" element={<Navigate to="/marketplace" replace />} />
                    <Route path="/incoming-requests" element={<Navigate to="/marketplace" replace />} />

                    {/* Profile route */}
                    <Route path="/profile" element={<MyProfile />} />

                    {/* Jobs History */}
                    <Route path="/jobs/history" element={<JobsHistory />} />

                    {/* Settings route */}
                    <Route path="/settings" element={
                      <SimplePermissionRoute module={MODULES.SETTINGS}>
                        <Settings />
                      </SimplePermissionRoute>
                    } />

                    {/* Catch-all: an unknown in-app URL (mistyped/bookmarked, or a sidebar
                        group name like /operations that isn't a route) redirects to the
                        dashboard instead of rendering a blank <main>. */}
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                  </RouteErrorBoundary>
                </Layout>
                </BillingGate>
              </ProtectedRoute>
            } />
          </Routes>
        </Suspense>
      </Router>
            </FeatureFlagProvider>
          </ThemeProvider>
        </IntegrationsProvider>
      </BrandProvider>
    </UserProvider>
    {process.env.REACT_APP_ENABLE_DEVTOOLS === 'true' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </DevToolsErrorBoundary>
  );
}

export default App;
